import { useEffect, useRef, useState } from "react";
import { formatEuro, WORK_TYPE_LABELS, WORK_TYPES, type WorkType } from "@klantpunt/shared";

export interface YearTotals {
  year: number;
  total: number;
  byType: Record<WorkType, number>;
}

/** Kort bedrag voor de as: € 12k, € 1,2 mln. */
export function shortEuro(cents: number): string {
  const e = cents / 100;
  if (e >= 1_000_000) return `€ ${(e / 1_000_000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} mln`;
  if (e >= 1_000) return `€ ${Math.round(e / 1_000).toLocaleString("nl-NL")}k`;
  return `€ ${Math.round(e).toLocaleString("nl-NL")}`;
}

/** Nette asverdeling: 0 en vier stappen tot boven het maximum. */
function ticks(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, k) => k * step);
}

/**
 * Gestapelde staven per jaar, één kleur per soort werk (vaste volgorde; zie --wt-* in styles.css).
 * Legenda erboven, hover/focus toont de opbouw van een jaar; de tabel eronder is de tekstversie.
 */
export function BudgetChart({ years, shown }: { years: YearTotals[]; shown: readonly WorkType[] }) {
  const [hover, setHover] = useState<number | null>(null);
  // Tekenbreedte = beschikbare breedte, zodat de grafiek meegroeit zonder dat tekst en hoogte opschalen.
  const wrap = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(280, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Hoogte groeit beperkt mee: plat op ultrawide noch te hoog op een telefoon.
  const H = Math.round(Math.min(420, Math.max(260, W * 0.25)));
  const pad = { l: 64, r: 8, t: 8, b: 28 };
  const max = Math.max(0, ...years.map((y) => y.total));
  const tk = ticks(max);
  const top = tk.at(-1) || 1;
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / Math.max(1, years.length);
  const bw = Math.min(40, slot * 0.6);
  // Jaartallen uitdunnen als ze anders over elkaar vallen (smal scherm).
  const every = Math.max(1, Math.ceil(40 / slot));
  const yOf = (v: number) => pad.t + ih - (v / top) * ih;
  const h = hover != null ? years.find((y) => y.year === hover) : null;

  return (
    <figure className="bchart">
      <ul className="bchart-legend" aria-label="Soort werk">
        {WORK_TYPES.filter((w) => shown.includes(w)).map((w) => (
          <li key={w}>
            <i className={`wt wt-${w}`} aria-hidden="true" />
            {WORK_TYPE_LABELS[w]}
          </li>
        ))}
      </ul>
      <div className="bchart-wrap" ref={wrap}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Begroting per jaar, gestapeld per soort werk">
          {tk.map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={yOf(v)} y2={yOf(v)} className="bchart-grid" />
              <text x={pad.l - 8} y={yOf(v)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
                {shortEuro(v)}
              </text>
            </g>
          ))}
          {years.map((y, k) => {
            const x = pad.l + k * slot + (slot - bw) / 2;
            let acc = 0;
            const segs = WORK_TYPES.filter((w) => shown.includes(w) && y.byType[w] > 0).map((w) => {
              const y0 = yOf(acc);
              acc += y.byType[w];
              const y1 = yOf(acc);
              return { w, y: y1, h: Math.max(0, y0 - y1) };
            });
            const label = `${y.year}: ${formatEuro(y.total)}${segs.length ? ` (${segs.map((s) => `${WORK_TYPE_LABELS[s.w]} ${formatEuro(y.byType[s.w])}`).join(", ")})` : ""}`;
            return (
              <g
                key={y.year}
                tabIndex={0}
                role="img"
                aria-label={label}
                className={`bchart-bar${hover === y.year ? " on" : ""}`}
                onMouseEnter={() => setHover(y.year)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(y.year)}
                onBlur={() => setHover(null)}
              >
                {/* Groter raakvlak dan de staaf zelf. */}
                <rect x={pad.l + k * slot} y={pad.t} width={slot} height={ih} className="bchart-hit" />
                {segs.map((s, i) => (
                  <rect
                    key={s.w}
                    x={x}
                    y={s.y}
                    width={bw}
                    height={s.h}
                    rx={i === segs.length - 1 ? 3 : 0}
                    className={`wt-${s.w} bchart-seg`}
                  />
                ))}
                {k % every === 0 && (
                  <text x={x + bw / 2} y={H - 8} className="bchart-axis" textAnchor="middle">
                    {y.year}
                  </text>
                )}
              </g>
            );
          })}
          <line x1={pad.l} x2={W - pad.r} y1={yOf(0)} y2={yOf(0)} className="bchart-base" />
        </svg>
        {h && (
          <div
            className="bchart-tip"
            role="status"
            style={{ left: `${((pad.l + (h.year - years[0]!.year + 0.5) * slot) / W) * 100}%` }}
          >
            <strong>{h.year}</strong> · {formatEuro(h.total)}
            {WORK_TYPES.filter((w) => shown.includes(w) && h.byType[w] > 0).map((w) => (
              <span key={w} className="block small">
                <i className={`wt wt-${w}`} aria-hidden="true" /> {WORK_TYPE_LABELS[w]}: {formatEuro(h.byType[w])}
              </span>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}
